import { MetadataEntityFieldData, MetadataEnumTypeData } from '../decorators/model/metadata.entity.field.data';
import { MetadataEntityFieldType } from '../decorators/model/metadata.entity.field.types';
import { MetadataEntityFieldValidatorType } from '../decorators/model/metadata.entity.field.validator';
import { ZodString, z } from 'zod';
import * as Enumerable from 'linq';
import { MetadataImportConfig } from './metadata.import.config';
import { DateUtility } from '../../../utilities/date.utility';
import { MetadataImportFieldBuilder } from './metadata.import.field.builder';

export interface MetadataImportValidatorResult {
    value: any;
    success: boolean;
    message: string;
}

const evaluateMetadataValidators = <T>(field: MetadataEntityFieldData, existingValidator: T) => {

    if (field.type == MetadataEntityFieldType.String ||
        field.type == MetadataEntityFieldType.StringAlpha ||
        field.type == MetadataEntityFieldType.StringAlphaNumeric) {
        let stringValidator = existingValidator as ZodString;
        for (const validator of field.validators) {
            switch (validator.type) {
                case MetadataEntityFieldValidatorType.MinLength: {
                    stringValidator = stringValidator.min(validator.data);
                    break;
                }
                case MetadataEntityFieldValidatorType.MaxLength: {
                    stringValidator = stringValidator.max(validator.data);
                    break;
                }
                case MetadataEntityFieldValidatorType.Pattern: {
                    stringValidator = stringValidator.regex(new RegExp(validator.data));
                    break;
                }
                default:
                    break;
            }
        }
        return stringValidator;
    }
    return existingValidator;
};

export function metadataImportCellValidator(field: MetadataEntityFieldData, value: any, config?: MetadataImportConfig): MetadataImportValidatorResult {

    let result: any;

    if (!field)
        return { success: false, message: 'Field Not Set', value };

    const entityFieldType = MetadataImportFieldBuilder.getEffectiveFieldType(field);

    try {
        switch (entityFieldType) {

            case MetadataEntityFieldType.Tag:
            case MetadataEntityFieldType.Lookup:
            case MetadataEntityFieldType.Parent:
            case MetadataEntityFieldType.String: {
                let validator = field.required ? z.string().trim().min(1) : z.string().trim().optional().nullable();
                validator = evaluateMetadataValidators(field, validator);
                result = validator.parse(value);
                break;
            }

            case MetadataEntityFieldType.StringAlphaNumeric:
            case MetadataEntityFieldType.StringAlpha: {

                const regex = entityFieldType === MetadataEntityFieldType.StringAlphaNumeric
                    ? /^\w+[\w\s-]*\w+$/
                    : /^[A-Za-z]+[\sA-Za-z]*[A-Za-z]+$/;

                let validator = field.required
                    ? z.string().trim().min(1).regex(regex)
                    : z.string().trim().optional().nullable().refine(x => x === null || x === undefined || x === '' || regex.test(x));
                validator = evaluateMetadataValidators(field, validator);
                result = validator.parse(value);
                break;
            }

            case MetadataEntityFieldType.Currency:
            case MetadataEntityFieldType.Number: {
                const zNumber = z
                    .preprocess(val => {
                        if (field.required && (val === '' || val === null || val === undefined))
                            return Number.NaN;
                        else
                            return Number(val);
                    }, z.number())

                const validator = field.required ? zNumber : zNumber.optional().nullable();
                result = validator.parse(value);
                break;
            }

            case MetadataEntityFieldType.Boolean: {

                const stringToBoolean = (value: unknown) => {

                    let safeValue: unknown = value;

                    if (typeof safeValue === 'string') {
                        safeValue = safeValue.toLowerCase().trim();
                    }

                    switch (safeValue) {
                        case true:
                        case 'true':
                        case '1':
                        case 1:
                        case 'on':
                        case 'yes':
                        case 'y':
                            safeValue = true;
                            break;

                        case false:
                        case 'false':
                        case '0':
                        case 0:
                        case '-1':
                        case 'off':
                        case 'no':
                        case 'n':
                            safeValue = false;
                            break;

                        default:
                            safeValue = undefined;
                            break;
                    }

                    if (!field.required && (value === '' || value === null || value === undefined))
                        safeValue = false;

                    return safeValue;

                }

                const zBoolean = z.preprocess(val => stringToBoolean(val), z.boolean());
                const validator = field.required ? zBoolean : zBoolean.optional().nullable();

                result = validator.parse(value);
                break;
            }

            case MetadataEntityFieldType.Enum: {

                const data = field.typeData as MetadataEnumTypeData[];
                const values = data.map(x => x.value?.toString()?.trim().toLowerCase()) as any;
                const zEnum = z.enum(values,);

                const validator = field.required
                    ? zEnum
                    : zEnum.optional().nullable();

                value = value?.trim() === ''
                    ? null
                    : value?.trim();

                result = validator.parse(value?.toLowerCase());

                const safeValue = Enumerable.from(field.typeData).select(x => x.value).firstOrDefault(x => x?.toString()?.trim().toLowerCase() === result);
                result = safeValue;

                break;
            }

            case MetadataEntityFieldType.Phone: {
                const regex = /^[ \(\)0-9+-]*$/;

                const validator = field.required
                    ? z.string().trim().min(1).regex(regex)
                    : z.string().trim().regex(regex).optional().nullable();

                result = validator.parse(value);
                break;
            }

            case MetadataEntityFieldType.Email: {

                const validator = field.required
                    ? z.string().trim().email().min(1)
                    : z.string().trim().email().optional().nullable().or(z.literal(''))

                result = validator.parse(value);
                break;
            }

            case MetadataEntityFieldType.Url: {
                const regex = new RegExp('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?');

                const validator = field.required
                    ? z.string().trim().min(1).regex(regex)
                    : z.string().trim().regex(regex).optional().nullable().or(z.literal(''));

                // const validator = field.required
                //     ? z.string().trim().url().min(1)
                //     : z.string().trim().url().optional().nullable().or(z.literal(''));

                result = validator.parse(value);
                break;
            }

            case MetadataEntityFieldType.DateTime:
            case MetadataEntityFieldType.Date: {

                const isCustomField = field.type === MetadataEntityFieldType.CustomField;

                value = value?.trim() === ''
                    ? null
                    : value?.trim();

                const configs = {
                    [MetadataEntityFieldType.DateTime]: config?.dateTimeFormat,
                    [MetadataEntityFieldType.Date]: config?.dateFormat,
                    [MetadataEntityFieldType.Time]: config?.timeFormat,
                }

                const format = configs[entityFieldType as MetadataEntityFieldType];

                // Preserve the wall-clock value as entered for every date kind — date-only, date+time,
                // and custom. Imported dates are stored timezone-independent (UTC zone, no offset shift),
                // so a value that carries a time (DateTime) must not be re-zoned by the local offset.
                const keepLocalTime = isCustomField
                    || entityFieldType === MetadataEntityFieldType.Date
                    || entityFieldType === MetadataEntityFieldType.DateTime;

                // This may already be an ISO date if it came from the client side
                let date = DateUtility.getDateTime(value, 'utc', keepLocalTime);

                // Fallback to the format the user wants
                if (!date?.isValid) {
                    date = DateUtility.fromFormat(value, format, 'utc', keepLocalTime);
                }

                // If the date is still invalid, try parsing the date without time
                if (!date?.isValid && entityFieldType === MetadataEntityFieldType.DateTime) {
                    // Try parsing the date without time
                    date = DateUtility.fromFormat(value, configs[MetadataEntityFieldType.Date], 'utc', keepLocalTime);
                }

                const validator = field.required
                    ? z.date()
                    : z.date().optional().nullable();

                result = validator.parse(date?.toJSDate());

                break;
            }

        }

        return {
            value: result ?? value,
            success: true,
        } as MetadataImportValidatorResult

    } catch (error) {

        const message = Enumerable
            .from(error?.issues ?? [])
            .select((x: any) => x.message)
            .toArray()
            .join(', ');

        return {
            value: value,
            success: false,
            message: message || error?.message,
        } as MetadataImportValidatorResult
    }
}
